<template>
  <UIDialog :is-open title="This asset has precision amounts" :z-index size="md" flat @close="handleClose">
    <div v-circular-tab class="precision-form">
      <div class="precision-form__items">
        <div class="precision-form__item-title">{{ accountTitle }}</div>
        <div class="precision-form__item-title">{{ amountTitle }}</div>
        <PrecisionFormItem v-for="(instance, index) in transactions" :key="instance.id" :instance="instance as TransactionClass" :index />
        <div class="precision-form__item-total">Total</div>
        <div class="precision-form__item-total" :class="totalClasses">{{ amountFormatter(totalAmount) }}</div>
      </div>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose :short @click="handleClose" />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { ModalClass, TransactionClass, TransactionSettings } from '@types'

import { displayAmount } from '@/entities/transactions/utils/helpers'

import { UIDialog } from '@ui/modals'
import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import PrecisionFormItem from './PrecisionFormItem.vue'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  amounts: [string, number][]
  createTransactionInstance: (accountId: string, assetId: string, amount: number) => TransactionClass | undefined
}

type Emits = {
  'complete': [data: [string, TransactionClass][]]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const transactions = ref<TransactionClass[]>([])

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const accountTitle = computed(() => props.entrySettings.account?.title)
const amountTitle = computed(() => props.entrySettings.amount?.title)

const totalAmount =  computed(() => {
  return transactions.value.reduce((acc, transaction) => {
    const amount = transaction.entry1().value?.amount
    return acc + (amount ?? 0)
  }, 0)
})

const totalClasses = computed(() => {
  return totalAmount.value <= 1 ? 'precision-form__item-total--positive' : 'precision-form__item-total--negative'
})

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings.amount
  if (!settings) return ''
  const isPercent = settings?.rule === 'percent'
  return displayAmount(value, isPercent)
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  // filter transactions with changed amounts
  // return only transactions with changed amounts
  const result = transactions.value.reduce((acc, transaction, index) => {
    const accountId = transaction.entry1().value?.account_id
    const assetId = transaction.entry1().value?.asset_id
    if (!accountId || !assetId) return acc
    const amount = transaction.entry1().value?.amount
    const oldAmount = props.amounts.find(([key]) => key.endsWith(`${accountId}-${assetId}`))?.[1]
    if (oldAmount === amount) return acc
    acc.push([props.amounts[index][0], transaction as TransactionClass])
    return acc
  }, [] as [string, TransactionClass][])
  emit('complete', result)
  handleClose()
}

onMounted(() => {
  // init transactions list
  const transactionsList = props.amounts.map(([key, amount]) => {
    const path = key.split('-')
    const accountId = path.at(-2)
    const assetId = path.at(-1)
    if (!accountId || !assetId) return
    return props.createTransactionInstance(accountId, assetId, amount)
  }).filter(instance => !!instance)
  transactions.value = transactionsList
})
</script>

<style scoped>
.precision-form {
  @apply pt-4 sm:pt-0;

  &__items {
    @apply grid grid-cols-[1fr_min-content] gap-y-2;

    :deep(> div) {
      @apply px-3 odd:pl-default even:pr-default;
      @apply whitespace-nowrap;
    }
  }

  &__item-title,
  &__item-total {
    @apply py-2;
    @apply bg-body-gray;
    @apply text-light-text;
    @apply text-sm font-medium;
  }

  &__item-title {
    @apply hidden sm:block;
  }

  &__item-total {
    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
